/**
 * Client side session state, backed by localStorage.
 *
 * The terminal is stateless: the user authenticates against the polyestershoppen.nl API, which issues a short lived
 * JWT access token (~5 minutes) and a single use refresh token (rotated on every refresh, the session ends 30 days
 * after logging in). Both are kept here, together with the selected warehouse user (from `GET /api/users`), the
 * environment and the shipping station.
 *
 * The locale of the last user that logged in (from `GET /api/v2/whoami`) is kept here as well, but outlives the session:
 * the login page and the PIN prompt are shown in that user's language (see `helpers/i18n.ts`). So are the languages
 * users picked on this terminal instead of the one of their account. The theme works the same: each user picks one on
 * this terminal, and the one of the last user stays for the login page (see `helpers/theme.ts`).
 */
import type {AccessTokenClaims} from './jwt';
import {decodeAccessToken, expiresWithin} from './jwt';
import type {User} from '../types/user';

export type Environment = 'development' | 'production';

export type Tokens = {
    accessToken: string;
    refreshToken: string;
    /** estimated end of the session in milliseconds since the epoch, the API does not expose it */
    refreshTokenExpiresAt: number;
};

/** The API ends a session 30 days after logging in; refreshing keeps the original expiry. */
const refreshTokenLifetime = 30 * 24 * 60 * 60 * 1000;

/** Without any activity for this long the session times out, also while the terminal was closed. */
export const idleTimeout = 5 * 60 * 1000;

/** Development is a deliberate choice for a single session: every session that ends falls back to this. */
export const defaultEnvironment: Environment = 'production';

const storageKeys = {
    tokens: 'shipping-terminal.tokens',
    polyshoppenUser: 'shipping-terminal.polyshoppen-user',
    environment: 'shipping-terminal.environment',
    shippingStation: 'shipping-terminal.shipping-station',
    lastActivity: 'shipping-terminal.last-activity',
    locale: 'shipping-terminal.locale',
    languages: 'shipping-terminal.languages',
    theme: 'shipping-terminal.theme',
    themes: 'shipping-terminal.themes',
} as const;

// (function declarations: Babel parses `.ts` files with JSX enabled, so `<T>() =>` would be ambiguous)
function parse<T>(value: string | null, fallback: T): T {
    try {
        return null === value ? fallback : (JSON.parse(value) as T);
    } catch {
        return fallback;
    }
}

function read<T>(key: string, fallback: T): T {
    try {
        return parse(window.localStorage.getItem(key), fallback);
    } catch {
        return fallback;
    }
}

const write = (key: string, value: unknown): void => {
    try {
        if (null === value || undefined === value) {
            window.localStorage.removeItem(key);
        } else {
            window.localStorage.setItem(key, JSON.stringify(value));
        }
    } catch {
        // storage unavailable (private mode, blocked storage, ...): state only lives in memory
    }
};

// left behind by the Laravel based terminal
write('shipping-terminal.current-user', null);

let tokens: Tokens | null = read<Tokens | null>(storageKeys.tokens, null);
let polyshoppenUser: User | null = read<User | null>(storageKeys.polyshoppenUser, null);

const toEnvironment = (value: unknown): Environment => ('development' === value ? 'development' : 'production');

let environment: Environment = toEnvironment(read<unknown>(storageKeys.environment, defaultEnvironment));

const toLocale = (value: unknown): string | null => ('string' === typeof value && '' !== value ? value : null);

let locale: string | null = toLocale(read<unknown>(storageKeys.locale, null));

/** What each user picked on this terminal (a language, a theme), by user id. */
type Picks = Record<string, string>;

const toPicks = (value: unknown): Picks =>
    null !== value && 'object' === typeof value && !Array.isArray(value) ? (value as Picks) : {};

/** The picks with the one of the user replaced, or left out for `null`. */
const withPick = (picks: Picks, userId: number, value: string | null): Picks => {
    const others = {...picks};

    delete others[userId];

    return null === value ? others : {...others, [userId]: value};
};

let languages: Picks = toPicks(read<unknown>(storageKeys.languages, {}));

export type Theme = 'light' | 'dark';

/** Before anybody picked a theme on this terminal, and for a user that never did. */
export const defaultTheme: Theme = 'light';

const toTheme = (value: unknown): Theme => ('dark' === value ? 'dark' : 'light');

let theme: Theme = toTheme(read<unknown>(storageKeys.theme, defaultTheme));
let themes: Picks = toPicks(read<unknown>(storageKeys.themes, {}));

const listeners = new Set<() => void>();

const notify = (): void => listeners.forEach(listener => listener());

/**
 * Get notified when any of the session state changes: the tokens, the user, the environment, the shipping station, the
 * locale or the theme.
 * Returns the unsubscribe function. This is the `subscribe` of `useSyncExternalStore` (see `hooks/use-session.ts`);
 * the getters below are its snapshots, they return the same value until the state changes.
 */
export const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
};

// keep other tabs in sync: refresh tokens are single use, so a token rotated by another tab replaces ours, and a login
// there changes the language and the theme here as well (the shipping station is not synced, each tab keeps the one it
// picked)
window.addEventListener('storage', (event: StorageEvent): void => {
    if (storageKeys.tokens === event.key) {
        tokens = parse<Tokens | null>(event.newValue, null);
    } else if (storageKeys.polyshoppenUser === event.key) {
        polyshoppenUser = parse<User | null>(event.newValue, null);
    } else if (storageKeys.environment === event.key) {
        environment = toEnvironment(parse<unknown>(event.newValue, defaultEnvironment));
    } else if (storageKeys.locale === event.key) {
        locale = toLocale(parse<unknown>(event.newValue, null));
    } else if (storageKeys.languages === event.key) {
        languages = toPicks(parse<unknown>(event.newValue, {}));
    } else if (storageKeys.theme === event.key) {
        theme = toTheme(parse<unknown>(event.newValue, defaultTheme));
    } else if (storageKeys.themes === event.key) {
        themes = toPicks(parse<unknown>(event.newValue, {}));
    } else {
        return;
    }

    notify();
});

export const getTokens = (): Tokens | null => tokens;

/** Re-read the tokens from storage, they may have been rotated by another tab. */
export const reloadTokens = (): Tokens | null => {
    tokens = read<Tokens | null>(storageKeys.tokens, tokens);
    notify();

    return tokens;
};

/** Store tokens from `POST /api/v2/token` (a new session) or `POST /api/v2/token/refresh` (same session). */
export const setTokens = (accessToken: string, refreshToken: string, newSession: boolean): void => {
    tokens = {
        accessToken,
        refreshToken,
        refreshTokenExpiresAt: newSession || !tokens ? Date.now() + refreshTokenLifetime : tokens.refreshTokenExpiresAt,
    };
    write(storageKeys.tokens, tokens);
    notify();

    if (newSession) {
        touchActivity(true);
    }
};

let lastActivityWrite = 0;

/** Remember the user was active (throttled), so a session left behind in a closed terminal times out as well. */
export const touchActivity = (force = false): void => {
    const now = Date.now();

    if (force || now - lastActivityWrite > 10_000) {
        lastActivityWrite = now;
        write(storageKeys.lastActivity, now);
    }
};

export const clearTokens = (): void => {
    tokens = null;
    write(storageKeys.tokens, null);
    notify();
};

/** The logged in user according to the access token (also for an expired one), `null` when there is none. */
export const getCurrentUser = (): AccessTokenClaims | null => decodeAccessToken(tokens?.accessToken);

export const getPolyshoppenUser = (): User | null => polyshoppenUser;

export const setPolyshoppenUser = (user: User | null): void => {
    polyshoppenUser = user;
    write(storageKeys.polyshoppenUser, user);
    notify();
};

/** A user is selected and holds an unexpired access token, or a refresh token to get a new one with. */
export const isLoggedIn = (): boolean =>
    null !== polyshoppenUser &&
    null !== tokens &&
    ((!!tokens.refreshToken && Date.now() < tokens.refreshTokenExpiresAt) ||
        !expiresWithin(decodeAccessToken(tokens.accessToken), 0));

/**
 * End the session: forget the user and its tokens and go back to the default environment. The shipping station is kept,
 * and so is the locale: the login page stays in the language of the user that just left.
 */
export const clearSession = (): void => {
    clearTokens();
    setPolyshoppenUser(null);
    write(storageKeys.lastActivity, null);
    setEnvironment(defaultEnvironment);
};

export const getEnvironment = (): Environment => environment;

/**
 * The locale of the last user that logged in, as the API has it (`nl_NL`, `fr_BE`, ...), or the language they picked on
 * this terminal (`en`, see `getPickedLanguage`); `null` before the first login.
 */
export const getLocale = (): string | null => locale;

export const setLocale = (value: string): void => {
    if (value === locale) {
        return;
    }

    locale = value;
    write(storageKeys.locale, value);
    notify();
};

/** The language the user picked on this terminal, over the locale of their account; `null` when they did not. */
export const getPickedLanguage = (userId: number): string | null => toLocale(languages[userId]);

/** Pick a language for the user on this terminal, `null` to follow the locale of their account again. */
export const setPickedLanguage = (userId: number, language: string | null): void => {
    if (language === getPickedLanguage(userId)) {
        return;
    }

    languages = withPick(languages, userId, language);
    write(storageKeys.languages, languages);
    notify();
};

/**
 * The theme the terminal shows: the one the last user picked, also on the login page after they left (like the
 * locale); `defaultTheme` before anybody picked one.
 */
export const getTheme = (): Theme => theme;

export const setTheme = (value: Theme): void => {
    if (value === theme) {
        return;
    }

    theme = value;
    write(storageKeys.theme, value);
    notify();
};

/** The theme the user picked on this terminal, `null` when they never did. */
export const getPickedTheme = (userId: number): Theme | null =>
    Object.hasOwn(themes, userId) ? toTheme(themes[userId]) : null;

/** Pick a theme for the user on this terminal, `null` to forget it. */
export const setPickedTheme = (userId: number, value: Theme | null): void => {
    if (value === getPickedTheme(userId)) {
        return;
    }

    themes = withPick(themes, userId, value);
    write(storageKeys.themes, themes);
    notify();
};

export const setEnvironment = (value: Environment): void => {
    if (value === environment) {
        return;
    }

    environment = value;
    write(storageKeys.environment, value);
    notify();
};

// A session that timed out while the terminal was closed (or reloaded) ends now, and without a session the terminal
// always starts on the default environment.
if (null !== tokens && Date.now() - read<number>(storageKeys.lastActivity, 0) > idleTimeout) {
    clearSession();
} else if (!isLoggedIn()) {
    setEnvironment(defaultEnvironment);
}

/** Get notified when the environment changes, in this tab or another one; returns the unsubscribe function. */
export const onEnvironmentChange = (listener: (environment: Environment) => void): (() => void) => {
    let previous = environment;

    return subscribe(() => {
        if (previous !== environment) {
            previous = environment;
            listener(environment);
        }
    });
};

export type ShippingStation = {
    shippingStation: string;
    printerId: number | undefined;
};

/** The Laravel based terminal kept the shipping station in a `shipping-station` cookie (JSON), move it over once. */
const migrateShippingStationCookie = (): void => {
    try {
        const cookie = document.cookie.split('; ').find(part => part.startsWith('shipping-station='));

        if (!cookie) {
            return;
        }

        document.cookie = 'shipping-station=; path=/; max-age=0';

        if (null !== read<ShippingStation | null>(storageKeys.shippingStation, null)) {
            return;
        }

        let station: unknown = JSON.parse(decodeURIComponent(cookie.slice('shipping-station='.length)));

        if ('string' === typeof station) {
            station = JSON.parse(station);
        }

        const {shippingStation, printerId} = (station ?? {}) as Partial<ShippingStation>;

        if ('string' === typeof shippingStation && shippingStation) {
            write(storageKeys.shippingStation, {
                shippingStation,
                printerId: 'number' === typeof printerId ? printerId : undefined,
            });
        }
    } catch {
        // an unreadable cookie: the shipping station has to be picked again
    }
};

migrateShippingStationCookie();

let shippingStation: ShippingStation | null = read<ShippingStation | null>(storageKeys.shippingStation, null);

export const getShippingStation = (): ShippingStation | null => shippingStation;

export const setShippingStation = (station: ShippingStation | null): void => {
    shippingStation = station;
    write(storageKeys.shippingStation, station);
    notify();
};
